import { Box, Group, Pagination, Table, Text } from "@mantine/core"
import { EyeIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { IInboundOrder } from "../../apis/inbound-order"
import { DeliveryRowAction } from "../delivery/action-controls"
import { TableActions } from "../table"
import { InboundStatusBadge, SourceTypeBadge } from "./atoms"
import { InboundActionState } from "./inbound-actions"

type Props = Readonly<{
  rows: IInboundOrder[]
  selectedId: string | null
  onSelect: (id: string) => void
  viewState: Pick<InboundActionState, "allowed" | "reason">
  total?: number
  page?: number
  pageSize?: number
  onPageChange?: (page: number) => void
}>

export function InboundOrdersTable({ rows, selectedId, onSelect, viewState, total = 0, page = 1, pageSize = 20, onPageChange }: Props) {
  const { t } = useTranslation("inbound")
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Box>
      <Table.ScrollContainer minWidth={760}>
        <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead bg="gray.0">
            <Table.Tr>
              <Table.Th fz={14} w={96}>
                {t("table.action")}
              </Table.Th>
              <Table.Th fz={14}>{t("table.number")}</Table.Th>
              <Table.Th fz={14}>{t("table.source")}</Table.Th>
              <Table.Th fz={14}>{t("table.status")}</Table.Th>
              <Table.Th fz={14}>{t("table.destination")}</Table.Th>
              <Table.Th fz={14} ta="right">
                {t("table.itemCount")}
              </Table.Th>
              <Table.Th fz={14}>{t("table.createdAt")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((order) => (
              <Table.Tr key={order.id} onClick={() => onSelect(order.id)} bg={selectedId === order.id ? "gray.1" : undefined} style={{ cursor: "pointer" }}>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <TableActions>
                    <DeliveryRowAction label={t("table.detail")} icon={<EyeIcon size={18} />} state={viewState} onClick={() => onSelect(order.id)} />
                  </TableActions>
                </Table.Td>
                <Table.Td>
                  <Text fz={14} fw={700} ff="monospace">
                    {order.inboundNumber ?? "-"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <SourceTypeBadge sourceType={order.sourceType} />
                </Table.Td>
                <Table.Td>
                  <InboundStatusBadge status={order.status} />
                </Table.Td>
                <Table.Td>
                  <Text fz={14}>{order.toWarehouseName ?? t("table.warehouseNotFound")}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text fz={14} ff="monospace">
                    {order.itemCount ?? "-"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={14} ff="monospace">
                    {order.createdAt ? dayjs(order.createdAt).format("YYYY-MM-DD HH:mm") : "-"}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {pageCount > 1 && (
        <Group justify="center" p="md">
          <Pagination value={page} total={pageCount} onChange={onPageChange} size="sm" />
        </Group>
      )}
    </Box>
  )
}
